import copy from '../../copy/en/auth.json' with { type: 'json' };
/**
 * Route guards.
 *   RequireAuth  — a session must exist (else → /login, remembering `from`);
 *                  then loads `/me` for everything beneath it.
 *   RequireMfa   — when the server enforces aal2 and the session is aal1
 *                  (or any call answered `mfa_required`) → /mfa.
 *   RequireCapability — keeps direct navigation aligned with server RBAC.
 *   PortalLanding — chooses the first useful page the caller may open.
 *   RedirectIfAuthed — keeps signed-in users off the login page.
 */
import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import type { PortalCapability } from "../../shared/roles.js";
import { AccessDeniedScreen, FullPageSpinner } from "../components/ui/FullPage.js";
import { mfaRedirectNeeded } from "../lib/me.js";
import { MeProvider, useMe } from "./MeProvider.js";
import { useSession } from "./SessionProvider.js";
import { isJoinConfirmationFailure } from "./joinConfirmation.js";

export function RequireAuth() {
  const { status } = useSession();
  const location = useLocation();
  if (status === "loading") return <FullPageSpinner label={copy.guards.checkingYourSession} />;
  if (status === "signed-out") {
    const from = `${location.pathname}${location.search}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }
  return (
    <MeProvider>
      <Outlet />
    </MeProvider>
  );
}

export function RequireMfa() {
  const { me, mfaBlocked } = useMe();
  const { mfaChallengeRequested } = useSession();
  if (mfaBlocked || mfaChallengeRequested || mfaRedirectNeeded(me)) {
    return <Navigate to="/mfa" replace />;
  }
  return <Outlet />;
}

/**
 * Pick a stable landing page from capabilities already resolved by the
 * server. Returning null is deliberate: an unknown/future role must not be
 * bounced into a page that will issue a forbidden data request.
 */
export function preferredPortalPath(capabilities: readonly PortalCapability[]): string | null {
  if (capabilities.includes("view:all")) return "/";
  if (capabilities.includes("reps:read")) return "/rep-workspace";
  if (capabilities.includes("crm:work")) return "/crm";
  if (capabilities.includes("practices:view")) return "/practices";
  if (capabilities.includes("org:view")) return "/sales-organization";
  if (capabilities.includes("links:create")) return "/sales-organization/links";
  if (capabilities.includes("users:manage")) return "/admin";
  if (capabilities.includes("org:manage")) return "/admin/sales-roles";
  return null;
}

function NoAuthorizedLanding() {
  const { signOut } = useSession();
  return (
    <AccessDeniedScreen
      message={copy.guards.yourAccountIsActiveButItHas}
      onSignOut={() => void signOut(copy.guards.youHaveBeenSignedOut)}
    />
  );
}

/** The root route is Dashboard for global viewers and a safe role-aware landing otherwise. */
export function PortalLanding({ children }: { children: ReactNode }) {
  const { me, hasCap } = useMe();
  if (hasCap("view:all")) return <>{children}</>;
  const target = preferredPortalPath(me?.capabilities ?? []);
  return target ? <Navigate to={target} replace /> : <NoAuthorizedLanding />;
}

/**
 * Client presentation gate for direct URLs. The matching Express route must
 * still enforce the capability; this prevents avoidable forbidden requests
 * and misleading screens, not unauthorized data access by itself.
 */
export function RequireCapability({
  capability,
  fallbackTo,
  children,
}: {
  capability: PortalCapability;
  fallbackTo?: string;
  children?: ReactNode;
}) {
  const { me, hasCap } = useMe();
  const location = useLocation();
  if (hasCap(capability)) return children ? <>{children}</> : <Outlet />;

  const target = fallbackTo ?? preferredPortalPath(me?.capabilities ?? []);
  if (target && target !== location.pathname) return <Navigate to={target} replace />;
  return <NoAuthorizedLanding />;
}

export function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { status, joinConfirmation } = useSession();
  const location = useLocation();
  if (status === "loading") return <FullPageSpinner label={copy.guards.checkingYourSession} />;
  if (status === "signed-in" && !isJoinConfirmationFailure(joinConfirmation)) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== "/login" ? from : "/"} replace />;
  }
  return <>{children}</>;
}
